import { formatResearcherNames } from 'utils/researcherName';
import { Fragment, useMemo } from 'react';
import { stripHtml } from 'utils/string';

import {
  Table,
  Link,
  Clamp,
  PublicationsFooter,
  HighlightPopover,
  // PublicationAuthors,
} from 'components/common';
import {
  DocumentCategories,
  getDocumentIdentifierField,
} from 'components/common/HighlightPopover/helpers';
import { format } from 'utils/date';
import { formatCurrency } from 'utils/number';
import licenseType from 'models/licenseType';
import { useAuth } from 'hooks';

const perPage = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 25, label: '25 per page' },
  { value: 50, label: '50 per page' },
];

// const formatAuthorName = author => {
//   if (author.collectiveName)
//     return (
//       <div
//         title={author.collectiveName}
//         style={{
//           whiteSpace: 'nowrap',
//           textOverflow: 'ellipsis',
//           overflow: 'hidden',
//         }}
//       >
//         {author.collectiveName}
//       </div>
//     );
//   return `${author.lastName} ${author.initials}`;
// };

export const BuildConfig = (parentContainer, data, highlightQuery) => {
  const config = {};
  const { clinicalTrials, funding, posters, publications } = data;
  const { hasLicense } = useAuth();
  const hasOrgLicense = useMemo(
    () => hasLicense([licenseType.industry, licenseType.academic], true),
    [hasLicense]
  );
  const configs = {
    publications: {
      headers: [
        'Publication Title',
        // 'Authors',
        'Journal/Preprints',
        'Date Published',
        'Citations',
        'Matches',
      ],
      createRow: (r, i) => (
        <Fragment key={`${r.citationId}-${i}`}>
          <div>
            <Link to={`publication/${r.citationId}`}>
              <Clamp
                text={r.articleTitle}
                title={stripHtml(r.articleTitle)}
                isHtml
              />
            </Link>
          </div>
          {/* <PublicationAuthors
              authors={r.authors}
              authorCount={r.authors.length}
              formatName={formatAuthorName}
              profileIdKey='scileadsSuperResearcherId'
            /> */}
          <Clamp text={r.journalTitle} />
          <div>{format(r.date, 'Do MMM YYYY', 'Not Available')}</div>
          <div>{r.cited}</div>
          <div>
            <HighlightPopover
              parentRef={parentContainer}
              entityId={
                r[getDocumentIdentifierField(DocumentCategories.publication)]
              }
              entityType={DocumentCategories.publication}
              highlightQuery={highlightQuery}
            />
          </div>
        </Fragment>
      ),
    },
    posters: {
      headers: [
        'Tradeshow',
        'Year',
        'Title',
        'Session Type',
        'Role',
        'Matches',
      ],
      createRow: (r, i) => (
        <Fragment key={`${r.sciLeadsTradeShowId}-${r.sciLeadsPosterId}-${i}`}>
          <Clamp text={r.show} />
          <div>{r.year}</div>
          <Link to={`/poster/${r.sciLeadsPosterId}`}>
            <Clamp text={r.title} />
          </Link>
          <div>{r.sessionType}</div>
          <div>{!r.roleType ? 'Uncategorised' : r.roleType}</div>
          <div>
            <HighlightPopover
              parentRef={parentContainer}
              entityId={
                r[getDocumentIdentifierField(DocumentCategories.poster)]
              }
              entityType={DocumentCategories.poster}
              highlightQuery={highlightQuery}
            />
          </div>
        </Fragment>
      ),
    },
    funding: {
      headers: [
        'Funding Award Title',
        'Date Awarded',
        { label: 'Funding (USD)', style: { justifyContent: 'flex-end' } },
        'Funding Starts',
        'Funding Ends',
        'Description',
        'Matches',
      ],
      createRow: (r, i) => (
        <Fragment key={`${r.sciLeadsAwardId}`}>
          <div>
            <Link to={`/project/${r.sciLeadsAwardId}`}>
              <Clamp text={r.title} />
            </Link>
          </div>
          <div>{format(r.dateAwarded, 'Do MMM YYYY', 'Not Available')}</div>
          <div style={{ justifyContent: 'flex-end' }}>
            {r.fundingAmountUsd
              ? `$${formatCurrency(r.fundingAmountUsd)}`
              : '-'}
          </div>
          <div>{format(r.fundingStarts, 'Do MMM YYYY', 'Not Available')}</div>
          <div>{format(r.fundingEnds, 'Do MMM YYYY', 'Not Available')}</div>
          <Clamp lines={2} text={r.description} />
          <div>
            <HighlightPopover
              parentRef={parentContainer}
              entityId={
                r[getDocumentIdentifierField(DocumentCategories.funding)]
              }
              entityType={DocumentCategories.funding}
              highlightQuery={highlightQuery}
            />
          </div>
        </Fragment>
      ),
    },
    clinicalTrials: {
      headers: [
        'NCT ID',
        'Role',
        'First Posted',
        'Study Type',
        'Title',
        'Status',
        'Phase',
        'Sponsor',
        'Matches',
      ],
      createRow: (r, i) => (
        <Fragment key={r.nctId}>
          <div>
            <Link to={`/trial/${r.nctId}`}>{r.nctId}</Link>
          </div>
          <div>{r.researcherRole}</div>
          <div>{format(r.date, 'Do MMM YYYY', 'Not Available')}</div>
          <div>{r.studyType}</div>
          <Clamp text={r.title} />
          <div>{r.overallStatus}</div>
          <div>{r.phase}</div>
          {!hasOrgLicense && <div>{r.leadSponsor}</div>}
          {hasOrgLicense && r.leadSponsorSuperOrg && (
            <Link to={`/organisation/${r.leadSponsorSuperOrg}`}>
              {r.leadSponsor}
            </Link>
          )}
          {hasOrgLicense && !r.leadSponsorSuperOrg && (
            <div>{r.leadSponsor}</div>
          )}
          <div>
            <HighlightPopover
              parentRef={parentContainer}
              entityId={r[getDocumentIdentifierField(DocumentCategories.trial)]}
              entityType={DocumentCategories.trial}
              highlightQuery={highlightQuery}
            />
          </div>
        </Fragment>
      ),
    },
    researchers: {
      headers: ['Name', 'Email'],
      createRow: (r, i) => (
        <Fragment key={`${r.id}-${i}`}>
          <div>
            <Link to={`/researcher/${r.id}`}>
              {formatResearcherNames(r.firstName, r.lastName)}
            </Link>
          </div>
          <div>{r.researcherEmail}</div>
        </Fragment>
      ),
    },
  };

  if (funding && funding.length) {
    config.funding = {
      icon: 'award',
      content: (
        <Table
          key='funding-table'
          pageSizeOptions={perPage}
          config={configs.funding}
          data={funding}
          footerStyle={{ padding: '15px' }}
          paginate
        />
      ),
    };
  }
  if (posters && posters.length) {
    config.posters = {
      icon: 'image',
      content: (
        <Table
          pageSizeOptions={perPage}
          config={configs.posters}
          data={posters}
          footerStyle={{ padding: '15px' }}
          paginate
        />
      ),
    };
  }
  if (publications && publications.length) {
    config.publications = {
      icon: 'book',
      content: (
        <Table
          pageSizeOptions={perPage}
          config={configs.publications}
          data={publications}
          footerStyle={{ padding: '15px' }}
          footerInfo={
            <PublicationsFooter
              firstNamedCount={data.totalFirstNamedAuthorCount}
              lastNamedCount={data.totalLastNamedAuthorCount}
              totalCollabs={data.totalCollaboratorsOverall}
              impactAll={data.allTimeHIndexAverage}
              impactThree={data.threeYearHIndexAverage}
            />
          }
          paginate
        />
      ),
    };
  }
  if (clinicalTrials && clinicalTrials.length) {
    config.clinicalTrials = {
      icon: 'trial',
      content: (
        <Table
          pageSizeOptions={perPage}
          config={configs.clinicalTrials}
          data={clinicalTrials}
          footerStyle={{ padding: '15px' }}
          paginate
        />
      ),
    };
  }

  return config;
};
